/**
 * Admin Usage — the one bounded adoption surface (ATRU-06; COP-134 D4).
 *
 * "The data exists, only the surface is missing." `GET /usage/summary` already
 * aggregates the audit rows the hub writes anyway; this page is the operator's
 * read of them: per-track / per-user / per-route counts over a chosen window,
 * with the retention horizon stated beside the numbers.
 *
 * Bounded BY DESIGN — an adoption surface, not a BI product. Exactly two
 * controls (window preset, dimension) and one chart type. No free-date picker,
 * no compare-to-previous, no export, no drill-through, no chart-type chooser:
 * each is a BI feature ATRU-06 excludes.
 *
 * AUTHZ. `GET /usage/summary` gates on `requireAdmin` and reads under
 * `tf_admin` — whole-deployment totals are a wider disclosure than a scoped
 * event read, so the route fails closed on the admin role (deliberately
 * stricter than `GET /audit/events`). The page matches that: it is registered
 * under the `<RequireAdmin>` subtree in `App.tsx`, so a non-admin session never
 * renders it and never sees the nav entry.
 *
 * This file owns ONLY the fetch. Rendering lives in `usage-view.tsx` and the
 * formatting in `usage-ui-model.ts` so the unit tests can import them without
 * `@/lib/hub-client` (it constructs a HubClient at module load and throws under
 * vitest).
 */
import { useEffect, useState } from 'react';
import { HubClientError } from '@verybigthings/trackframe-hub-client';
import type { UsageSummaryGroupBy, UsageSummaryResponse } from '@verybigthings/trackframe-shared/schemas';
import { hub } from '@/lib/hub-client';
import { USAGE_DEFAULT_WINDOW_DAYS, buildUsageQuery } from './usage-ui-model';
import { UsageView, type UsageViewState } from './usage-view';

export function Usage() {
  const [windowDays, setWindowDays] = useState<number>(USAGE_DEFAULT_WINDOW_DAYS);
  const [groupBy, setGroupBy] = useState<UsageSummaryGroupBy>('track');
  const [state, setState] = useState<UsageViewState>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });
    hub
      .usageSummary({ query: buildUsageQuery(windowDays, groupBy) })
      // Annotated, not cast: the endpoint's typed response must stay assignable
      // to the view's data shape, so a contract change surfaces here.
      .then((res: UsageSummaryResponse) => {
        if (!cancelled) setState({ kind: 'ready', data: res });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          kind: 'error',
          httpStatus: err instanceof HubClientError ? err.httpStatus : null,
          message: (err as Error).message ?? 'Failed to load usage',
        });
      });
    return () => {
      cancelled = true;
    };
  }, [windowDays, groupBy]);

  return (
    <UsageView
      state={state}
      windowDays={windowDays}
      groupBy={groupBy}
      onWindowDays={setWindowDays}
      onGroupBy={setGroupBy}
    />
  );
}
